import React, { useState, useEffect } from 'react';
import {
  LayoutDashboard, Layers, Target, Sparkles, TrendingUp, BarChart2,
  Download, Upload, ChevronRight, ChevronLeft, Wallet, CreditCard,
  Menu, X, Compass, LogOut, Eye, EyeOff, Receipt, KeyRound,
} from 'lucide-react';
import { AppProvider, useAppState } from './store/AppContext';
import { PrivacyProvider, usePrivacy } from './store/PrivacyContext';
import { Dashboard } from './components/Dashboard';
import { AssetManager } from './components/AssetManager';
import { BucketManager } from './components/BucketManager';
import { StrategyDashboard } from './components/StrategyDashboard';
import { FundDetailPage } from './components/FundDetailPage';
import { FundFireCalculator } from './components/FundFireCalculator';
import { AISnippetParser } from './components/AISnippetParser';
import { CashFlowProjection } from './components/CashFlowProjection';
import { AnalyticsHub } from './components/AnalyticsHub';
import { LiabilityManager } from './components/LiabilityManager';
import { StrategyHub } from './components/StrategyHub';
import { TransactionLogger } from './components/TransactionLogger';
import { QuickTransactionModal, QuickTransactionFAB } from './components/QuickTransactionModal';
import { LoginPage } from './components/LoginPage';
import { ChangePasswordPage } from './components/ChangePasswordPage';
import { SplashScreen } from './components/SplashScreen';
import { ThemeToggle } from './components/ui/ThemeToggle';
import { getTotalNAV } from './utils/calculations';
import { formatVND } from './utils/format';
import { exportState, importState } from './utils/storage';
import { useTheme } from './hooks/useTheme';
import { supabase } from './lib/supabaseClient';

type Tab = 'dashboard' | 'assets' | 'buckets' | 'snippet' | 'cashflow' | 'analytics' | 'liabilities' | 'strategy-hub' | 'transactions';
type NavItem = { id: Tab; label: string; Icon: React.ComponentType<{ size?: number }> };

const NAV: NavItem[] = [
  { id: 'dashboard',    label: 'Tổng quan',          Icon: LayoutDashboard },
  { id: 'transactions', label: 'Giao dịch',           Icon: Receipt },
  { id: 'assets',       label: 'Tài sản',             Icon: Layers },
  { id: 'buckets',      label: 'Chiến lược Quỹ',      Icon: Target },
  { id: 'liabilities',  label: 'Quản lý Nợ',          Icon: CreditCard },
  { id: 'snippet',      label: 'AI Parser',            Icon: Sparkles },
  { id: 'cashflow',     label: 'Dòng tiền',            Icon: TrendingUp },
  { id: 'analytics',    label: 'Phân tích',            Icon: BarChart2 },
  { id: 'strategy-hub', label: 'Reflection & Notes',  Icon: Compass },
];

const SUBTITLES: Record<Tab, string> = {
  dashboard:    'Snapshot toàn gia sản theo thời gian thực',
  transactions: 'Ghi chép thu chi · Dòng tiền thực tế · MoBu Flow',
  assets:       'Theo dõi, giao dịch và phân loại tài sản',
  buckets:      'Bucketing — phân bổ tài sản theo mục tiêu',
  liabilities:  'Khoản vay, tài sản ròng và nghĩa vụ tài chính',
  snippet:      'Dán bảng giá — AI tự động trích xuất và cập nhật',
  cashflow:     'Dự báo thu chi 12 tháng tới',
  analytics:       'Báo cáo lợi nhuận, cảnh báo lệch chuẩn, sổ cái',
  'strategy-hub':  'Hiến chương · Nhật ký quản trị · AI Tư vấn — Reflection & Notes',
};

const SIDEBAR_W   = 220;
const SIDEBAR_COL = 56;

function AppInner({ onLogout, userEmail }: { onLogout: () => void; userEmail: string }) {
  const [tab,           setTab]          = useState<Tab>('dashboard');
  const [fundDetailId,  setFundDetailId]  = useState<string | null>(null);
  const [fireCalcId,    setFireCalcId]    = useState<string | null>(null);
  const [drawerOpen,    setDrawerOpen]   = useState(false);  // mobile drawer
  const [collapsed,     setCollapsed]    = useState(false);  // desktop collapse
  const [showChangePw,  setShowChangePw] = useState(false);  // in-app đổi mật khẩu
  const [syncErrors,  setSyncErrors]  = useState<{ source: string; message: string; details: string; hint: string; isAuthError?: boolean; isWarning?: boolean }[]>([]);
  const [dbStatus, setDbStatus] = useState<'checking' | 'connected' | 'error'>('checking');

  // Kiểm tra kết nối Supabase
  useEffect(() => {
    let cancelled = false;
    async function ping() {
      setDbStatus('checking');
      try {
        const { error } = await supabase.from('assets').select('id').limit(1);
        if (!cancelled) setDbStatus(error ? 'error' : 'connected');
      } catch {
        if (!cancelled) setDbStatus('error');
      }
    }
    ping();
    const interval = setInterval(ping, 30_000);
    return () => { cancelled = true; clearInterval(interval); };
  }, []);

  // Lắng nghe lỗi sync từ storage.ts — tự dismiss sau 15s, user cũng có thể bấm X
  useEffect(() => {
    const handler = (e: Event) => {
      const detail = (e as CustomEvent).detail;
      setSyncErrors(prev => {
        const isDupe = prev.some(p => p.source === detail.source && p.message === detail.message);
        if (isDupe) return prev;
        return [...prev.slice(-4), detail];
      });
      // Tự xoá sau 15 giây
      setTimeout(() => {
        setSyncErrors(prev => prev.filter(p => p.message !== detail.message));
      }, 15_000);
    };
    const okHandler = () => {
      setSyncErrors(prev => prev.filter(e => !e.isWarning));
    };
    window.addEventListener('wealthos:sync-error', handler);
    window.addEventListener('wealthos:sync-ok', okHandler);
    return () => {
      window.removeEventListener('wealthos:sync-error', handler);
      window.removeEventListener('wealthos:sync-ok', okHandler);
    };
  }, []);
  const { state, dispatch, syncing } = useAppState();
  const { isDark, toggle }  = useTheme();
  const totalNAV = getTotalNAV(state.assets);
  const sidebarW = collapsed ? SIDEBAR_COL : SIDEBAR_W;

  const handleImport = () => {
    const el = document.createElement('input');
    el.type = 'file'; el.accept = '.json';
    el.onchange = (e) => {
      const f = (e.target as HTMLInputElement).files?.[0];
      if (f) importState(f, (s) => dispatch({ type: 'SET_STATE', payload: s }));
    };
    el.click();
  };

  const [highlightTxId,    setHighlightTxId]    = useState<string | null>(null);
  const [quickTxOpen,      setQuickTxOpen]      = useState(false);

  // Shift+N mở form giao dịch nhanh
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (e.shiftKey && e.key === 'N' && !e.ctrlKey && !e.metaKey) {
        const tag = (document.activeElement as HTMLElement)?.tagName;
        if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT') return;
        e.preventDefault();
        setQuickTxOpen(true);
      }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, []);

  const handleNav = (id: Tab) => { setTab(id); setFundDetailId(null); setFireCalcId(null); setDrawerOpen(false); };
  const handleNavigateToFund = (fundId: string) => { setTab('buckets'); setFundDetailId(fundId); setFireCalcId(null); setDrawerOpen(false); };
  const handleNavigateToFire = (fundId: string) => { setTab('buckets'); setFireCalcId(fundId); setFundDetailId(null); setDrawerOpen(false); };
  const handleNavigateToTx  = (txId: string)    => { setHighlightTxId(txId); setTab('transactions'); setDrawerOpen(false); };

  // ── Sidebar nav buttons ───────────────────────────────────────────────────
  const NavButtons = ({ iconOnly }: { iconOnly: boolean }) => (
    <>
      {!iconOnly && (
        <p style={{
          fontSize: 10, fontWeight: 600, color: 'var(--tx-5)',
          letterSpacing: '0.08em', padding: '6px 10px 4px',
          textTransform: 'uppercase',
        }}>Menu</p>
      )}
      {NAV.map((item) => {
        const active = tab === item.id;
        return (
          <button
            key={item.id}
            onClick={() => handleNav(item.id)}
            title={iconOnly ? item.label : undefined}
            style={{
              width: '100%', display: 'flex', alignItems: 'center',
              gap: iconOnly ? 0 : 10,
              padding: iconOnly ? '9px 0' : '8px 10px',
              justifyContent: iconOnly ? 'center' : 'flex-start',
              borderRadius: 9, marginBottom: 2, cursor: 'pointer',
              textAlign: 'left',
              backgroundColor: active ? 'rgba(16,185,129,0.12)' : 'transparent',
              border: `1px solid ${active ? 'rgba(16,185,129,0.2)' : 'transparent'}`,
              transition: 'background-color 0.12s, border-color 0.12s',
            }}
            onMouseEnter={(e) => {
              if (!active) (e.currentTarget as HTMLButtonElement).style.backgroundColor = 'var(--bg-row-hover)';
            }}
            onMouseLeave={(e) => {
              if (!active) (e.currentTarget as HTMLButtonElement).style.backgroundColor = 'transparent';
            }}
          >
            <span style={{ color: active ? '#10b981' : 'var(--tx-4)', flexShrink: 0, display: 'flex' }}>
              <item.Icon size={16} />
            </span>
            {!iconOnly && (
              <span style={{
                fontSize: 13, fontWeight: active ? 500 : 400,
                color: active ? 'var(--tx-1)' : 'var(--tx-3)',
                whiteSpace: 'nowrap', overflow: 'hidden',
              }}>
                {item.label}
              </span>
            )}
          </button>
        );
      })}
    </>
  );

  // ── Sidebar footer actions ────────────────────────────────────────────────
  const FooterActions = ({ iconOnly }: { iconOnly: boolean }) => (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
      {/* Export / Import / Theme row */}
      <div style={{
        display: 'flex',
        flexDirection: iconOnly ? 'column' : 'row',
        gap: 6, alignItems: 'center',
      }}>
        {[
          { label: 'Xuất', title: 'Xuất dữ liệu', Icon: Download, action: () => exportState(state) },
          { label: 'Nhập', title: 'Nhập dữ liệu', Icon: Upload,   action: handleImport },
        ].map((btn) => (
          <button
            key={btn.label}
            onClick={btn.action}
            title={iconOnly ? btn.title : undefined}
            style={{
              flex: iconOnly ? undefined : 1,
              width: iconOnly ? 32 : undefined,
              height: iconOnly ? 32 : undefined,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              gap: 5,
              padding: iconOnly ? 0 : '7px 0',
              borderRadius: 8, fontSize: 11, cursor: 'pointer',
              background: 'var(--bg-card-el)', color: 'var(--tx-4)',
              border: '1px solid var(--bd)', transition: 'background 0.12s',
            }}
            onMouseEnter={(e) => { (e.currentTarget as HTMLButtonElement).style.background = 'var(--bg-row-hover)'; }}
            onMouseLeave={(e) => { (e.currentTarget as HTMLButtonElement).style.background = 'var(--bg-card-el)'; }}
          >
            <btn.Icon size={iconOnly ? 13 : 11} />
            {!iconOnly && btn.label}
          </button>
        ))}
        <ThemeToggle isDark={isDark} onToggle={toggle} />
      </div>

      {/* Đổi mật khẩu button */}
      <button
        onClick={() => setShowChangePw(true)}
        title="Đổi mật khẩu"
        style={{
          width: '100%',
          display: 'flex', alignItems: 'center', justifyContent: iconOnly ? 'center' : 'flex-start',
          gap: 7, padding: iconOnly ? '7px 0' : '8px 10px',
          borderRadius: 8, fontSize: 12, cursor: 'pointer',
          background: 'transparent', color: 'var(--tx-4)',
          border: '1px solid transparent', transition: 'all 0.12s', marginBottom: 4,
        }}
        onMouseEnter={(e) => {
          const b = e.currentTarget as HTMLButtonElement;
          b.style.background = 'rgba(0,209,255,0.06)';
          b.style.borderColor = 'rgba(0,209,255,0.18)';
          b.style.color = 'rgba(0,209,255,0.8)';
        }}
        onMouseLeave={(e) => {
          const b = e.currentTarget as HTMLButtonElement;
          b.style.background = 'transparent';
          b.style.borderColor = 'transparent';
          b.style.color = 'var(--tx-4)';
        }}
      >
        <KeyRound size={13} />
        {!iconOnly && <span>Đổi mật khẩu</span>}
      </button>

      {/* Logout button */}
      <button
        onClick={onLogout}
        title="Đăng xuất"
        style={{
          width: '100%',
          display: 'flex', alignItems: 'center', justifyContent: iconOnly ? 'center' : 'flex-start',
          gap: 7, padding: iconOnly ? '7px 0' : '8px 10px',
          borderRadius: 8, fontSize: 12, cursor: 'pointer',
          background: 'transparent', color: 'var(--tx-4)',
          border: '1px solid transparent', transition: 'all 0.12s',
        }}
        onMouseEnter={(e) => {
          const b = e.currentTarget as HTMLButtonElement;
          b.style.background = 'rgba(248,113,113,0.08)';
          b.style.borderColor = 'rgba(248,113,113,0.2)';
          b.style.color = '#f87171';
        }}
        onMouseLeave={(e) => {
          const b = e.currentTarget as HTMLButtonElement;
          b.style.background = 'transparent';
          b.style.borderColor = 'transparent';
          b.style.color = 'var(--tx-4)';
        }}
      >
        <LogOut size={13} />
        {!iconOnly && (
          <span style={{ flex: 1, textAlign: 'left', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
            {userEmail ? `Đăng xuất · ${userEmail.split('@')[0]}` : 'Đăng xuất'}
          </span>
        )}
      </button>
    </div>
  );

  // ── Shared sidebar shell ──────────────────────────────────────────────────
  const SidebarShell = ({ iconOnly, onClose }: { iconOnly: boolean; onClose?: () => void }) => (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%', overflow: 'hidden' }}>
      {/* Wordmark */}
      <div style={{
        padding: iconOnly ? '18px 0 14px' : '18px 18px 14px',
        borderBottom: '1px solid var(--bd-sub)',
        display: 'flex', alignItems: 'center',
        justifyContent: iconOnly ? 'center' : 'space-between',
        gap: 10, flexShrink: 0,
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <div style={{
            width: 30, height: 30, borderRadius: 9, flexShrink: 0,
            background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            boxShadow: 'var(--logo-glow)',
          }}>
            <Wallet size={14} color="#fff" />
          </div>
          {!iconOnly && (
            <div>
              <div style={{ fontSize: 12, fontWeight: 700, color: 'var(--tx-1)', letterSpacing: '0.08em', whiteSpace: 'nowrap' }}>
                WEALTH OS
              </div>
              <div style={{ fontSize: 10, color: 'var(--tx-5)', letterSpacing: '0.05em', marginTop: 1 }}>
                PROJECT BLUE
              </div>
            </div>
          )}
        </div>
        {onClose && (
          <button onClick={onClose} style={{
            background: 'none', border: 'none', cursor: 'pointer',
            color: 'var(--tx-4)', display: 'flex', padding: 4, flexShrink: 0,
          }}>
            <X size={16} />
          </button>
        )}
      </div>

      {/* Nav */}
      <nav style={{ flex: 1, padding: '10px', overflowY: 'auto' }}>
        <NavButtons iconOnly={iconOnly} />
      </nav>

      {/* Footer */}
      <div style={{
        padding: iconOnly ? '12px 8px' : '12px 14px',
        borderTop: '1px solid var(--bd-sub)', flexShrink: 0,
      }}>

        <FooterActions iconOnly={iconOnly} />

        {/* Collapse toggle (desktop only — no onClose) */}
        {!onClose && (
          <button
            onClick={() => setCollapsed((c) => !c)}
            title={collapsed ? 'Mở rộng menu' : 'Thu gọn menu'}
            style={{
              width: '100%', marginTop: 8,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              padding: '6px 0', borderRadius: 8, cursor: 'pointer',
              background: 'transparent', color: 'var(--tx-5)',
              border: '1px solid var(--bd)', transition: 'background 0.12s',
              fontSize: 11, gap: 5,
            }}
            onMouseEnter={(e) => { (e.currentTarget as HTMLButtonElement).style.background = 'var(--bg-row-hover)'; }}
            onMouseLeave={(e) => { (e.currentTarget as HTMLButtonElement).style.background = 'transparent'; }}
          >
            {collapsed
              ? <ChevronRight size={13} />
              : <><ChevronLeft size={13} /><span>Thu gọn</span></>
            }
          </button>
        )}
      </div>
    </div>
  );

  const CurrentIcon = NAV.find((n) => n.id === tab)?.Icon ?? LayoutDashboard;

  return (
    <div
      className="app-layout"
      style={{
        '--sidebar-w': `${sidebarW}px`,
        backgroundColor: 'var(--bg-app)',
        fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
      } as React.CSSProperties}
    >
      {/* ── Desktop Sidebar (grid col 1, in-flow) ── */}
      <aside
        className="sidebar-desktop"
        style={{
          backgroundColor: 'var(--bg-sidebar)',
          borderRight: '1px solid var(--bd)',
          overflow: 'hidden',
          minWidth: 0,
        }}
      >
        <SidebarShell iconOnly={collapsed} />
      </aside>

      {/* ── Mobile: Backdrop overlay ── */}
      {drawerOpen && (
        <div
          className="mobile-only"
          onClick={() => setDrawerOpen(false)}
          style={{
            position: 'fixed', inset: 0,
            backgroundColor: 'rgba(0,0,0,0.5)',
            zIndex: 30,
          }}
        />
      )}

      {/* ── Mobile: Drawer (position fixed, outside grid) ── */}
      <aside
        className="mobile-only"
        style={{
          position: 'fixed', top: 0, bottom: 0, left: 0,
          width: SIDEBAR_W,
          backgroundColor: 'var(--bg-sidebar)',
          borderRight: '1px solid var(--bd)',
          zIndex: 40,
          transform: drawerOpen ? 'translateX(0)' : `translateX(-${SIDEBAR_W}px)`,
          transition: 'transform 0.22s ease',
          overflow: 'hidden',
          paddingTop: 'env(safe-area-inset-top, 0px)',
        }}
      >
        <SidebarShell iconOnly={false} onClose={() => setDrawerOpen(false)} />
      </aside>

      {/* ── Main (grid col 2) ── */}
      <main style={{
        display: 'flex', flexDirection: 'column',
        overflow: 'hidden', minWidth: 0,
        backgroundColor: 'var(--bg-app)',
        gridColumn: 2,  /* luôn ở column 2, tránh bị đẩy sang column 1 (0px) khi sidebar display:none */
      }}>

        {/* ── Topbar ── */}
        {/* paddingTop = env(safe-area-inset-top) để đẩy nội dung xuống dưới
            vùng notch / Dynamic Island trên iPhone; zIndex 10 đảm bảo nút luôn
            nhận được sự kiện tap, không bị các layer khác che phủ.             */}
        <header style={{
          flexShrink: 0,
          paddingTop: 'env(safe-area-inset-top, 0px)',
          borderBottom: '1px solid var(--bd)',
          backgroundColor: 'var(--bg-topbar)',
          position: 'relative', zIndex: 10,
        }}>
          {/* Thanh điều hướng thực: cố định 56px, nằm bên dưới vùng safe area */}
          <div style={{ height: 56, display: 'flex', alignItems: 'center', padding: '0 16px', gap: 10 }}>
          {/* Mobile: hamburger button */}
          <button
            className="mobile-only"
            onClick={() => setDrawerOpen(true)}
            style={{
              flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center',
              width: 32, height: 32, borderRadius: 8,
              background: 'none', border: '1px solid var(--bd)',
              color: 'var(--tx-3)', cursor: 'pointer',
            }}
          >
            <Menu size={16} />
          </button>

          {/* Mobile: mini brand mark */}
          <div className="mobile-only" style={{ flexShrink: 0, gap: 8 }}>
            <div style={{
              width: 24, height: 24, borderRadius: 7,
              background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              boxShadow: 'var(--logo-glow)',
            }}>
              <Wallet size={11} color="#fff" />
            </div>
          </div>

          {/* Page title + subtitle */}
          <div style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, minWidth: 0 }}>
              {/* Desktop only: breadcrumb icon */}
              <span className="desktop-only" style={{ color: 'var(--tx-5)', flexShrink: 0 }}>
                <CurrentIcon size={14} />
              </span>
              <span style={{
                fontSize: 14, fontWeight: 600, color: 'var(--tx-1)',
                whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
              }}>
                {tab === 'buckets' && fireCalcId
                  ? `FIRE — ${state.buckets.find(b => b.id === fireCalcId)?.name ?? 'Chiến lược'}`
                  : tab === 'buckets' && fundDetailId
                    ? (state.buckets.find(b => b.id === fundDetailId)?.name ?? 'Chi tiết Quỹ')
                    : NAV.find((n) => n.id === tab)?.label}
              </span>
              <span className="sm-inline" style={{ color: 'var(--tx-5)', flexShrink: 0 }}>
                <ChevronRight size={12} />
              </span>
              <span className="sm-block" style={{
                fontSize: 11, color: 'var(--tx-4)',
                whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
              }}>
                {SUBTITLES[tab]}
              </span>
            </div>
          </div>

          {/* Right: status + theme (mobile only — desktop theme is in sidebar) */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexShrink: 0 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
              {syncing ? (
                <>
                  <div style={{ width: 6, height: 6, borderRadius: '50%', backgroundColor: '#38bdf8',
                    animation: 'pulse 0.8s ease-in-out infinite' }} />
                  <span className="lg-inline" style={{ fontSize: 11, color: '#38bdf8', whiteSpace: 'nowrap' }}>
                    Đang lưu…
                  </span>
                </>
              ) : (
                <>
                  {dbStatus === 'checking' && (
                    <div style={{ width: 6, height: 6, borderRadius: '50%', backgroundColor: '#f59e0b',
                      animation: 'pulse 1.2s ease-in-out infinite', opacity: 0.8 }} />
                  )}
                  {dbStatus === 'connected' && (
                    <div style={{ width: 6, height: 6, borderRadius: '50%', backgroundColor: '#10b981',
                      boxShadow: '0 0 6px #10b981' }} />
                  )}
                  {dbStatus === 'error' && (
                    <div style={{ width: 6, height: 6, borderRadius: '50%', backgroundColor: '#ef4444',
                      boxShadow: '0 0 6px #ef4444' }} />
                  )}
                  <span className="lg-inline" style={{ fontSize: 11, color: 'var(--tx-5)', whiteSpace: 'nowrap' }}>
                    {dbStatus === 'checking'  && 'Đang kết nối…'}
                    {dbStatus === 'connected' && 'Đã kết nối'}
                    {dbStatus === 'error'     && 'Offline'}
                  </span>
                </>
              )}
            </div>
            <PrivacyToggleBtn />
            <div className="mobile-only">
              <ThemeToggle isDark={isDark} onToggle={toggle} />
            </div>
          </div>
          </div>{/* /inner 56px bar */}
        </header>

        {/* ── Sync error banners — sticky, không tự dismiss ── */}
        {syncErrors.map((err, i) => {
          const isWarning = err.isWarning;
          const bg  = isWarning ? 'rgba(245,158,11,0.95)' : 'rgba(239,68,68,0.95)';
          const bdr = isWarning ? 'rgba(245,158,11,0.5)'  : 'rgba(239,68,68,0.5)';
          return (
            <div key={i} style={{
              position: 'sticky', top: 0, zIndex: 50,
              padding: '10px 20px',
              display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 12,
              background: bg, backdropFilter: 'blur(8px)',
              borderBottom: `1px solid ${bdr}`,
            }}>
              <div style={{ flex: 1, minWidth: 0 }}>
                <span style={{ fontSize: 12, fontWeight: 700, color: '#fff' }}>
                  {isWarning ? '⚠ Cảnh báo' : '✕ Lỗi lưu dữ liệu'} [{err.source}]:
                </span>{' '}
                <span style={{ fontSize: 12, color: 'rgba(255,255,255,0.92)' }}>{err.message}</span>
                {err.details && (
                  <div style={{ fontSize: 11, color: 'rgba(255,255,255,0.75)', marginTop: 3 }}>
                    {err.details}
                  </div>
                )}
                {err.hint && (
                  <div style={{ fontSize: 11, color: 'rgba(255,255,255,0.75)', marginTop: 2 }}>
                    💡 {err.hint}
                  </div>
                )}
              </div>
              <button
                onClick={() => setSyncErrors(prev => prev.filter((_, j) => j !== i))}
                title="Đóng thông báo"
                style={{ background: 'rgba(255,255,255,0.2)', border: 'none', borderRadius: 6, color: '#fff', cursor: 'pointer', fontSize: 14, lineHeight: 1, flexShrink: 0, padding: '4px 8px', fontWeight: 700 }}
              >
                ×
              </button>
            </div>
          );
        })}

        {/* ── Content ── */}
        <div className="app-content-pad" style={{ flex: 1, overflowY: 'auto', overflowX: 'hidden', padding: '20px 20px 32px',
          /* paddingBottom ghi đè padding shorthand: cộng thêm Home Indicator iPhone */
          paddingBottom: 'calc(32px + env(safe-area-inset-bottom, 0px))' }}>
          {tab === 'dashboard'    && <Dashboard onNavigateToFund={handleNavigateToFund} />}
          {tab === 'transactions' && <TransactionLogger highlightTxId={highlightTxId} onHighlightConsumed={() => setHighlightTxId(null)} />}
          {tab === 'assets'      && <AssetManager onNavigateToTx={handleNavigateToTx} />}
          {tab === 'buckets'     && !fundDetailId && !fireCalcId && <StrategyDashboard onNavigateToFund={handleNavigateToFund} onNavigateToFire={handleNavigateToFire} />}
          {tab === 'buckets'     && fundDetailId  && !fireCalcId && <FundDetailPage fundId={fundDetailId} onBack={() => setFundDetailId(null)} />}
          {tab === 'buckets'     && fireCalcId    && <FundFireCalculator fundId={fireCalcId} onBack={() => setFireCalcId(null)} />}
          {tab === 'snippet'     && <AISnippetParser />}
          {tab === 'cashflow'    && <CashFlowProjection />}
          {tab === 'analytics'   && <AnalyticsHub />}
          {tab === 'liabilities'   && <LiabilityManager />}
          {tab === 'strategy-hub'  && <StrategyHub />}
        </div>
      </main>

      {/* ── Quick Transaction FAB ── */}
      <QuickTransactionFAB onClick={() => setQuickTxOpen(true)} />
      <QuickTransactionModal open={quickTxOpen} onClose={() => setQuickTxOpen(false)} />

      {/* In-app: Đổi mật khẩu overlay */}
      {showChangePw && (
        <ChangePasswordPage isInApp onDone={() => setShowChangePw(false)} />
      )}
    </div>
  );
}

export default function App() {
  const [session,    setSession]    = useState<any>(undefined); // undefined = loading
  const [userEmail,  setUserEmail]  = useState('');
  const [isRecovery, setIsRecovery] = useState(false); // true khi user đến từ reset email

  useEffect(() => {
    supabase.auth.getSession().then(({ data }) => {
      setSession(data.session ?? null);
      setUserEmail(data.session?.user?.email ?? '');
    });
    const { data: { subscription } } = supabase.auth.onAuthStateChange((event, sess) => {
      setSession(sess ?? null);
      setUserEmail(sess?.user?.email ?? '');
      if (event === 'PASSWORD_RECOVERY') {
        setIsRecovery(true);
      }
      if (event === 'SIGNED_OUT') {
        setIsRecovery(false);
      }
    });
    return () => subscription.unsubscribe();
  }, []);

  const handleLogout = async () => {
    await supabase.auth.signOut();
  };

  // Auth check chưa xong → splash tĩnh
  if (session === undefined) return <SplashScreen message="MoBu đang sẵn sàng..." />;

  // Chưa đăng nhập
  if (!session) return <LoginPage />;

  // Recovery session (đến từ "Quên mật khẩu?" email link) → đổi mật khẩu
  if (isRecovery) {
    return <ChangePasswordPage onDone={() => setIsRecovery(false)} />;
  }

  // Đã đăng nhập bình thường
  return (
    <PrivacyProvider>
      <AppProvider>
        <AppInner onLogout={handleLogout} userEmail={userEmail} />
      </AppProvider>
    </PrivacyProvider>
  );
}

// ── Privacy toggle button ──────────────────────────────────────────────────────
function PrivacyToggleBtn() {
  const { isPrivacy, togglePrivacy } = usePrivacy();
  return (
    <button
      onClick={togglePrivacy}
      title={isPrivacy ? 'Hiển thị số liệu' : 'Ẩn số liệu'}
      style={{
        display:         'flex',
        alignItems:      'center',
        justifyContent:  'center',
        width:           32,
        height:          32,
        borderRadius:    8,
        border:          '1px solid var(--bd)',
        background:      isPrivacy ? 'rgba(96,165,250,0.1)' : 'var(--bg-card-el)',
        color:           isPrivacy ? '#60a5fa' : 'var(--tx-4)',
        cursor:          'pointer',
        transition:      'all 0.2s ease',
        flexShrink:      0,
      }}
    >
      {isPrivacy ? <EyeOff size={15} /> : <Eye size={15} />}
    </button>
  );
}
